import { FormErrors, FormOwnProps, HiddenConfig, SelectConfig, TextConfig } from '@qfa/forms';
import {
  FormViewType,
  Lane,
  LaneDirection,
  LaneInput,
  TravelDirection,
} from '@kinetic-ui/data-layer';
import { TFunction } from 'i18next';
import { nanoid } from 'nanoid';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LaneFormProperties } from './const';
import { SetState } from 'types';
import { ViewOptions } from 'components/forms';
import { useForm } from 'kineticUtils';

const getLaneDirectionOptions = (t: TFunction) =>
  Object.values(LaneDirection).map((direction) => ({
    label: t(direction),
    value: direction,
  }));

const getTravelDirectionOptions = (t: TFunction) =>
  Object.values(TravelDirection).map((direction) => ({
    label: t(direction),
    value: direction,
  }));

type LaneForm = {
  values: [
    values: Partial<LaneInput>,
    setValues: SetState<Partial<LaneInput>>,
    reset: (newValues?: Partial<LaneInput>) => void
  ];
  isDirty: boolean;
  formProps: FormOwnProps<LaneInput>;
  errors: {
    hasErrors: boolean;
    errors: FormErrors<Lane>;
    setErrors: SetState<FormErrors<Lane>>;
  };
  isSaveDisabled: boolean;
  viewProps: ViewOptions;
  setDirtyFields: SetState<string[]>;
};

export const useLaneForm = (
  initialData: Partial<LaneInput> | undefined,
  isNew?: boolean,
  disabled?: boolean
): LaneForm => {
  const { t } = useTranslation(['common', 'locations']);

  const id = useMemo(() => initialData?.id ?? nanoid(), [initialData?.id]);

  const {
    props: formProps,
    values: [values, setValues, reset],
    errors: [hasErrors, errors, setErrors],
    dirty: [isDirty, _dirtyFields, setDirtyFields],
    isSaveDisabled,
  } = useForm<LaneInput>({
    readOnly: disabled,
    fields: [
      HiddenConfig({
        id: LaneFormProperties.ID + initialData?.id,
        name: LaneFormProperties.ID,
        required: true,
        defaultValue: id,
      }),
      HiddenConfig({
        id: LaneFormProperties.GEOMETRY + initialData?.id,
        name: LaneFormProperties.GEOMETRY,
        required: true,
      }),
      HiddenConfig({
        id: LaneFormProperties.ID + initialData?.id,
        name: LaneFormProperties.ID,
        required: true,
      }),
      TextConfig({
        id: LaneFormProperties.DESCRIPTION + initialData?.id,
        name: LaneFormProperties.DESCRIPTION,
        required: true,
        label: t('description'),
        fieldSize: 'half',
      }),
      SelectConfig({
        id: LaneFormProperties.LANE_DIRECTION + initialData?.id,
        name: LaneFormProperties.LANE_DIRECTION,
        options: getLaneDirectionOptions(t),
        label: t('laneDirection'),
        sortOrder: 'asc',
      }),
      SelectConfig({
        id: LaneFormProperties.TRAVEL_DIRECTION + initialData?.id,
        name: LaneFormProperties.TRAVEL_DIRECTION,
        required: false,
        options: getTravelDirectionOptions(t),
        label: t('travelDirection'),
        sortOrder: 'asc',
      }),
    ],
    defaultValues: {
      ...initialData,
    },
  });

  const viewProps: ViewOptions = useMemo(
    () => ({
      type: FormViewType.Embedded,
      viewId: `lane-${id}`,
      paths: [],
      sectionsOptions: [
        {
          fieldNames: [
            LaneFormProperties.DESCRIPTION,
            LaneFormProperties.LANE_DIRECTION,
            LaneFormProperties.TRAVEL_DIRECTION,
          ],
          name: t('details'),
          hashRoute: `details-${id}`,
          sectionId: `details-${id}`,
        },
      ],
    }),
    [id, t]
  );

  return {
    viewProps,
    formProps,
    isDirty,
    setDirtyFields,
    values: [values, setValues, reset],
    errors: { hasErrors, errors, setErrors },
    isSaveDisabled,
  };
};
